/**
 * Formulations d'ancres du maillage interne. MODULE PARTAGE.
 *
 * ⛔⛔ POURQUOI CE FICHIER EST EN .mjs ET PAS EN .ts.
 * Il est importe par DEUX consommateurs qui ne parlent pas le meme langage :
 *   - `app/utils/cityDirectory.ts`, qui genere les liens du site ;
 *   - `scripts/audit-maillage.mjs`, qui mesure le graphe au build et le bloque.
 * Dupliquer les gabarits dans le script d'audit aurait garanti la derive : le
 * jour ou quelqu'un ajoute une formulation ici, la mesure deviendrait fausse
 * sans que rien n'echoue. Un seul fichier, deux lecteurs.
 *
 * ⛔ TOUT EST DETERMINISTE. Aucun hasard, aucune date, aucun etat. Un tirage
 * aleatoire changerait les ancres a chaque compilation, donc le HTML de 730
 * pages a chaque deploiement, pour rien.
 */

/**
 * Empreinte stable d'une chaine. FNV-1a, puis un brassage final.
 *
 * ⛔ Le brassage n'est pas cosmetique. La version precedente (h * 31 + code)
 * gardait des paquets une fois ramenee modulo 9 : 🟢 le 21/09, /chauffeur/
 * le-cannet recevait 6 de ses 14 liens sous la meme formulation (43 %) alors
 * que les graines etaient toutes differentes. Un hachage multiplicatif simple
 * se repartit mal sur un petit modulo.
 */
export const empreinte = (graine) => {
  let h = 2166136261;
  for (let i = 0; i < graine.length; i += 1) {
    h ^= graine.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  h ^= h >>> 16;
  h = Math.imul(h, 2246822507);
  h ^= h >>> 13;
  return h >>> 0;
};

/** Choisit un element de facon stable pour une graine donnee. */
export const choisir = (liste, graine) => liste[empreinte(graine) % liste.length];

/**
 * Preposition correcte devant un nom de ville.
 *
 * ⛔ Le gabarit precedent ecrivait « à ${n} » sans condition, ce qui produisait
 * « Chauffeur privé à Le Cannet » et « à Le Lavandou » partout sur le site.
 * « La Turbie », « La Colle-sur-Loup », « La Croix-Valmer » sont corrects avec
 * « à » et tombent dans le cas par defaut.
 */
export const aVille = (n) => {
  if (n.startsWith("Le ")) return `au ${n.slice(3)}`;
  if (n.startsWith("Les ")) return `aux ${n.slice(4)}`;
  return `à ${n}`;
};

/** Neuf formulations FR, huit EN, huit AR. Toutes vraies, toutes naturelles. */
export const GABARITS_CHAUFFEUR = {
  fr: [
    (n, a) => `Chauffeur privé ${a}`,
    (n, a) => `VTC ${a}`,
    (n, a) => `Voiture avec chauffeur ${a}`,
    (n) => `Chauffeur privé ${n}`,
    (n, a) => `Service de chauffeur ${a}`,
    (n, a) => `Réserver un chauffeur ${a}`,
    (n, a) => `Transferts avec chauffeur ${a}`,
    (n) => `Chauffeur ${n}, tarifs et trajets`,
    (n, a) => `Mise à disposition avec chauffeur ${a}`,
  ],
  en: [
    (n) => `Private chauffeur in ${n}`,
    (n) => `Car service in ${n}`,
    (n) => `Chauffeur service ${n}`,
    (n) => `Book a chauffeur in ${n}`,
    (n) => `Private car service in ${n}`,
    (n) => `${n} chauffeur rates`,
    (n) => `Airport transfers in ${n}`,
    (n) => `Chauffeur hire ${n}`,
  ],
  ar: [
    (n) => `سائق خاص في ${n}`,
    (n) => `سيارة مع سائق في ${n}`,
    (n) => `خدمة سائق في ${n}`,
    (n) => `حجز سائق في ${n}`,
    (n) => `أسعار السائق في ${n}`,
    (n) => `نقل مع سائق في ${n}`,
    (n) => `سائق خاص ${n}`,
    (n) => `خدمة النقل في ${n}`,
  ],
};

export const GABARITS_LOCATION = {
  fr: [
    (n, a) => `Location de voiture ${a}`,
    (n, a) => `Louer une voiture ${a}`,
    (n, a) => `Location de véhicule ${a}`,
    (n) => `Location de voiture ${n}`,
    (n, a) => `Voitures à louer ${a}`,
    (n, a) => `Tarifs de location ${a}`,
    (n, a) => `Location avec livraison ${a}`,
    (n) => `Louer une voiture ${n}`,
    (n, a) => `Location courte durée ${a}`,
  ],
  en: [
    (n) => `Car rental in ${n}`,
    (n) => `Rent a car in ${n}`,
    (n) => `Car hire in ${n}`,
    (n) => `Vehicle rental in ${n}`,
    (n) => `${n} car rental rates`,
    (n) => `Rent a car ${n}`,
    (n) => `Car rental with delivery in ${n}`,
    (n) => `Short-term car rental in ${n}`,
  ],
  ar: [
    (n) => `تأجير سيارة في ${n}`,
    (n) => `استئجار سيارة في ${n}`,
    (n) => `تأجير سيارات في ${n}`,
    (n) => `أسعار التأجير في ${n}`,
    (n) => `تأجير سيارة ${n}`,
    (n) => `استئجار سيارة ${n}`,
    (n) => `تأجير سيارة مع التوصيل في ${n}`,
    (n) => `تأجير قصير المدى في ${n}`,
  ],
};

/**
 * Ancre vers une page ville, formulee selon la page SOURCE.
 * `nom` est le nom affiche de la ville cible, pas son slug.
 */
export const ancreVille = (kind, nom, locale, indiceGabarit) => {
  const jeu = kind === "chauffeur" ? GABARITS_CHAUFFEUR : GABARITS_LOCATION;
  const liste = jeu[locale] ?? jeu.fr;
  return liste[indiceGabarit % liste.length](nom, aVille(nom));
};

/* ══ LE GRAPHE. Deplace ici le 21/09/2026 depuis cityDirectory.ts, pour que
 * `scripts/audit-maillage.mjs` EXECUTE le maillage au lieu d'en relire une
 * copie. Une copie aurait derive des le premier ajout de ville. ══ */

/** Slugs des 15 pages "location voiture <ville>". */
export const RENTAL_CITY_SLUGS = [
  "cannes",
  "nice",
  "monaco",
  "antibes",
  "saint-tropez",
  "mandelieu-la-napoule",
  "le-cannet",
  "juan-les-pins",
  "grasse",
  "mougins",
  "cagnes-sur-mer",
  "saint-laurent-du-var",
  "menton",
  "frejus",
  "hyeres",
  "toulon",
  "saint-raphael",
  "sainte-maxime",
  "vence",
  "villeneuve-loubet",
  "biot",
  "valbonne",
  "vallauris-golfe-juan",
  "le-lavandou",
  "cavalaire-sur-mer",
  "bormes-les-mimosas",
  "villefranche-sur-mer",
];

/** Slug → nom propre (graphie accentuée). Repli : title-case du slug. */
export const CITY_NAME = {
  cannes: "Cannes",
  nice: "Nice",
  monaco: "Monaco",
  antibes: "Antibes",
  "saint-tropez": "Saint-Tropez",
  "cote-d-azur": "Côte d’Azur",
  "saint-jean-cap-ferrat": "Saint-Jean-Cap-Ferrat",
  eze: "Èze",
  "villefranche-sur-mer": "Villefranche-sur-Mer",
  mougins: "Mougins",
  "sophia-antipolis": "Sophia Antipolis",
  "mandelieu-la-napoule": "Mandelieu-la-Napoule",
  "le-cannet": "Le Cannet",
  "vallauris-golfe-juan": "Vallauris Golfe-Juan",
  "juan-les-pins": "Juan-les-Pins",
  "theoule-sur-mer": "Théoule-sur-Mer",
  grasse: "Grasse",
  valbonne: "Valbonne",
  biot: "Biot",
  "villeneuve-loubet": "Villeneuve-Loubet",
  "cagnes-sur-mer": "Cagnes-sur-Mer",
  "saint-laurent-du-var": "Saint-Laurent-du-Var",
  "saint-paul-de-vence": "Saint-Paul-de-Vence",
  vence: "Vence",
  "beaulieu-sur-mer": "Beaulieu-sur-Mer",
  "cap-d-ail": "Cap-d’Ail",
  beausoleil: "Beausoleil",
  menton: "Menton",
  "roquebrune-cap-martin": "Roquebrune-Cap-Martin",
  "la-turbie": "La Turbie",
  "tourrettes-sur-loup": "Tourrettes-sur-Loup",
  "la-colle-sur-loup": "La Colle-sur-Loup",
  "mouans-sartoux": "Mouans-Sartoux",
  opio: "Opio",
  "roquefort-les-pins": "Roquefort-les-Pins",
  "saint-raphael": "Saint-Raphaël",
  frejus: "Fréjus",
  "sainte-maxime": "Sainte-Maxime",
  grimaud: "Grimaud",
  "port-grimaud": "Port-Grimaud",
  cogolin: "Cogolin",
  gassin: "Gassin",
  "la-croix-valmer": "La Croix-Valmer",
  ramatuelle: "Ramatuelle",
  "cavalaire-sur-mer": "Cavalaire-sur-Mer",
  "le-lavandou": "Le Lavandou",
  "bormes-les-mimosas": "Bormes-les-Mimosas",
  hyeres: "Hyères",
  toulon: "Toulon",
};

/** Groupes de villes proches (corridors réels de la Côte d'Azur et du Var). */
export const CLUSTERS = [
  [
    "cannes",
    "le-cannet",
    "mandelieu-la-napoule",
    "mougins",
    "theoule-sur-mer",
    "vallauris-golfe-juan",
  ],
  [
    "antibes",
    "juan-les-pins",
    "biot",
    "villeneuve-loubet",
    "vallauris-golfe-juan",
    // 🟢 MESURE 2026-09-21 : sophia-antipolis n'etait declaree que dans
    // SOLO_NEIGHBORS, donc elle CITAIT trois villes sans qu'aucune la cite en
    // retour : 4 liens entrants pour 895 impressions sur 12 mois. Elle est
    // geographiquement dans le triangle Antibes-Biot-Valbonne, sa place est ici.
    "sophia-antipolis",
  ],
  ["grasse", "mouans-sartoux", "opio", "valbonne", "roquefort-les-pins"],
  [
    "vence",
    "saint-paul-de-vence",
    "tourrettes-sur-loup",
    "la-colle-sur-loup",
    "cagnes-sur-mer",
  ],
  ["nice", "cagnes-sur-mer", "saint-laurent-du-var", "villeneuve-loubet"],
  ["villefranche-sur-mer", "beaulieu-sur-mer", "saint-jean-cap-ferrat", "eze"],
  [
    "monaco",
    "cap-d-ail",
    "beausoleil",
    "la-turbie",
    "roquebrune-cap-martin",
    "eze",
  ],
  ["menton", "roquebrune-cap-martin", "beausoleil"],
  ["saint-raphael", "frejus", "theoule-sur-mer"],
  [
    "saint-tropez",
    "sainte-maxime",
    "grimaud",
    "port-grimaud",
    "cogolin",
    "gassin",
    "ramatuelle",
    "la-croix-valmer",
  ],
  [
    "cavalaire-sur-mer",
    "le-lavandou",
    "bormes-les-mimosas",
    "hyeres",
    "toulon",
  ],
];

/** Villes hors-cluster (région/technopole) → voisines explicites. */
export const SOLO_NEIGHBORS = {
  "sophia-antipolis": ["valbonne", "biot", "antibes"],
  "cote-d-azur": ["cannes", "nice", "monaco"],
};

/**
 * Nom ARABE de chaque ville.
 *
 * ⛔ Aucune translitteration inventee ici : chaque nom est RECOLTE dans le
 * titre de la page arabe de cette ville, deja publiee sur le site. Une ville
 * absente de cette table garde son nom latin dans l'ancre arabe, ce que le
 * site faisait deja partout avant le 21/09/2026 : c'est moins bon, mais c'est
 * vrai, alors qu'une graphie devinee ne le serait pas.
 */
export const CITY_NAME_AR = {
  "antibes": "أنتيب",
  "beaulieu-sur-mer": "بوليو سور مير",
  "beausoleil": "بوسولاي",
  "biot": "بيو",
  "bormes-les-mimosas": "بورم لي ميموزا",
  "cagnes-sur-mer": "كاني-سور-مير",
  "cannes": "كان",
  "cap-d-ail": "كاب داي",
  "cavalaire-sur-mer": "كافالير سور مير",
  "cogolin": "كوغولان",
  "cote-d-azur": "كوت دازور",
  "eze": "إيز",
  "frejus": "فريجوس",
  "gassin": "غاسان",
  "grasse": "غراس",
  "grimaud": "غريمو",
  "hyeres": "يير",
  "juan-les-pins": "جوان لي بان",
  "la-colle-sur-loup": "لا كول سور لو",
  "la-croix-valmer": "لا كروا فالمير",
  "la-turbie": "لا توربي",
  "le-cannet": "لو كانيه",
  "le-lavandou": "لو لافاندو",
  "mandelieu-la-napoule": "ماندليو لا نابول",
  "menton": "مانتون",
  "monaco": "موناكو",
  "mouans-sartoux": "موان سارتو",
  "mougins": "موجان",
  "nice": "نيس",
  "opio": "أوبيو",
  "port-grimaud": "بور غريمو",
  "ramatuelle": "راماتويل",
  "roquebrune-cap-martin": "روكبرون كاب مارتان",
  "roquefort-les-pins": "روكفور لي بان",
  "saint-jean-cap-ferrat": "سان جان كاب فيرا",
  "saint-laurent-du-var": "سان-لوران-دو-فار",
  "saint-paul-de-vence": "سان بول دو فانس",
  "saint-raphael": "سان رافاييل",
  "saint-tropez": "سان تروبيه",
  "sainte-maxime": "سانت ماكسيم",
  "sophia-antipolis": "صوفيا أنتيبوليس",
  "theoule-sur-mer": "تيول سور مير",
  "toulon": "تولون",
  "tourrettes-sur-loup": "تورّيت سور لو",
  "valbonne": "فالبون",
  "vallauris-golfe-juan": "فالوري غولف جوان",
  "vence": "فانس",
  "villefranche-sur-mer": "فيلفرانش سور مير",
  "villeneuve-loubet": "فيلنوف-لوبيه",
};
export const RENTAL_SET = new Set(RENTAL_CITY_SLUGS);

const titleCase = (slug) =>
  slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

export const cityName = (slug, locale) =>
  (locale === "ar" ? CITY_NAME_AR[slug] : undefined) ??
  CITY_NAME[slug] ??
  titleCase(slug);

/** Voisines de chaque ville : union des clusters, puis les cas isoles. */
export const CITY_NEIGHBORS = (() => {
  const acc = {};
  for (const cluster of CLUSTERS) {
    for (const a of cluster) {
      acc[a] = acc[a] ?? new Set();
      for (const b of cluster) if (b !== a) acc[a].add(b);
    }
  }
  const out = {};
  for (const key of Object.keys(acc)) out[key] = Array.from(acc[key]);
  for (const [key, val] of Object.entries(SOLO_NEIGHBORS)) out[key] = val;
  return out;
})();

/**
 * Second anneau : les voisines des voisines, apres les voisines directes.
 *
 * ⛔⛔ POURQUOI ON S'ARRETE A HUIT, ET PAS A QUARANTE.
 * 🟢 MESURE 2026-09-21 : le silo compte 49 pages ville chauffeur. Si chaque
 * page en cite K, chaque ville recoit K liens en moyenne. Pour atteindre les
 * 40 liens entrants que mesure l'etude du coffre, il faudrait K = 40, donc un
 * maillage QUASI COMPLET des 49 villes : le meme bloc de 40 liens sur chaque
 * page du silo. C'est exactement le motif que cette meme etude condamne, et
 * que le lien de pied de page avait deja produit ici le 20/09.
 *
 * Les pages qui atteignent 40 y arrivent parce qu'elles sont citees DEHORS du
 * silo ville : evenements, vehicules, hubs, silo location. Une ville
 * secondaire ne peut pas y arriver par le maillage de proximite, et c'est une
 * contrainte de taille de site, pas un reglage. Huit est le compromis : deux
 * fois plus de liens qu'avant, toujours un bloc « villes proches » lisible.
 */
export const POLES_SATURES = new Set(["cannes", "nice", "monaco"]);

export const voisinesEtendues = (slug, combien) => {
  const vues = new Set([slug]);
  const out = [];
  let front = [slug];
  let anneau = 1;
  while (front.length && out.length < combien) {
    const suivant = [];
    for (const a of front) {
      for (const b of CITY_NEIGHBORS[a] ?? []) {
        if (vues.has(b)) continue;
        vues.add(b);
        // ⛔ Au PREMIER anneau, un pole est une vraie voisine : Le Cannet cite
        // Cannes, c'est juste. Au SECOND, c'est du remplissage, et ca pousse
        // le pole au-dela du plafond : 🟢 le 21/09, elargir l'anneau sans
        // cette exception a mis /chauffeur/cannes a 51 liens, au-dessus des
        // 50 ou l'etude du coffre mesure une inversion de l'effet.
        if (anneau > 1 && POLES_SATURES.has(b)) continue;
        out.push(b);
        suivant.push(b);
        if (out.length >= combien) return out;
      }
    }
    front = suivant;
    anneau += 1;
  }
  return out;
};

/**
 * ⛔ `enSlugDe` est injecte : la table FR↔EN vit dans le registre chauffeur,
 * qui est du TypeScript avec des alias `@/`. L'injecter garde ce module
 * lisible par Node sans compilation, donc executable par l'audit.
 */
export const chauffeurHref = (slug, locale, enSlugDe) =>
  locale === "en"
    ? `/en/chauffeur/${enSlugDe(slug) ?? slug}`
    : locale === "ar"
      ? `/ar/chauffeur/${slug}`
      : `/chauffeur/${slug}`;

export const rentalHref = (slug, locale) =>
  locale === "en"
    ? `/en/location/car-rental-${slug}`
    : locale === "ar"
      ? `/ar/location/location-voiture-${slug}`
      : `/location/location-voiture-${slug}`;

/** Ancre CHAUFFEUR vers `cible`, formulee selon la page `source`. */
/**
 * RANGS : pour chaque page cible, l'ordre de ses citeurs de cluster.
 *
 * ⛔⛔ POURQUOI UN RANG ET PAS SEULEMENT UNE EMPREINTE.
 * Tirer un gabarit au hasard pour chaque lien, meme de facon stable, laisse
 * des paquets. 🟢 SIMULATION 2026-09-21, 4 000 tirages : avec 8 gabarits et
 * 13 liens, une page a 12,6 % de chances de depasser le tiers sur une seule
 * formulation, sans qu'aucune ecriture soit en cause. A 20 liens, 6,3 %.
 * Un controle qui echoue une fois sur dix pour rien finit par etre desactive.
 *
 * Le rang supprime le hasard : le 1er citeur prend le gabarit 1, le 2e le 2,
 * et ainsi de suite en tournant. La repartition devient exacte. Ce qui n'est
 * pas un lien de cluster (un lien editorial ecrit dans un fichier) garde
 * l'empreinte : on ne connait pas la liste de ses freres.
 */
const RANGS = (() => {
  const m = new Map();
  const pose = (kind, cible, source) => {
    const cle = `${kind}>${cible}`;
    if (!m.has(cle)) m.set(cle, new Map());
    const s = m.get(cle);
    if (!s.has(source)) s.set(source, s.size);
  };
  for (const v of Object.keys(CITY_NEIGHBORS).sort()) {
    if (RENTAL_SET.has(v)) pose("location", v, v);
    for (const n of voisinesEtendues(v, 8)) pose("chauffeur", n, v);
  }
  for (const v of [...RENTAL_CITY_SLUGS].sort()) {
    pose("chauffeur", v, `${v}-loc`);
    for (const n of voisinesEtendues(v, 20)
      .filter((x) => RENTAL_SET.has(x))
      .slice(0, 6)) {
      pose("location", n, `${v}-loc`);
    }
  }
  return m;
})();

/** Indice du gabarit : le rang du citeur s'il en a un, l'empreinte sinon. */
const indice = (kind, cible, locale, source) => {
  const r = RANGS.get(`${kind}>${cible}`)?.get(source);
  const decalage = locale === "en" ? 3 : locale === "ar" ? 6 : 0;
  return r === undefined
    ? empreinte(`${source}>${cible}>${locale}>${kind}`)
    : r + decalage;
};

export const ancreChauffeur = (cible, locale, source) =>
  ancreVille(
    "chauffeur",
    cityName(cible, locale),
    locale,
    indice("chauffeur", cible, locale, source),
  );

/** Ancre LOCATION vers `cible`, formulee selon la page `source`. */
export const ancreLocation = (cible, locale, source) =>
  ancreVille(
    "location",
    cityName(cible, locale),
    locale,
    indice("location", cible, locale, source),
  );

/** Page CHAUFFEUR ville → jumeau location (si dispo) + 4 villes proches. */
export const clusterChauffeur = (slug, locale, enSlugDe) => {
  const links = [];
  if (RENTAL_SET.has(slug)) {
    links.push({
      href: rentalHref(slug, locale),
      label: ancreLocation(slug, locale, slug),
    });
  }
  // 8 et non 3 : avec 3, une ville en bout de cluster n'etait citee par
  // personne. 🟢 MESURE 2026-09-21 : /chauffeur/antibes ne recevait que
  // 8 liens internes nommes pour 596 impressions en position 32.
  voisinesEtendues(slug, 8).forEach((n) =>
    links.push({
      href: chauffeurHref(n, locale, enSlugDe),
      label: ancreChauffeur(n, locale, slug),
    }),
  );
  return links;
};

/** Page LOCATION ville → jumeau chauffeur + 4 villes de location proches. */
export const clusterLocation = (slug, locale, enSlugDe) => {
  const links = [
    {
      href: chauffeurHref(slug, locale, enSlugDe),
      label: ancreChauffeur(slug, locale, `${slug}-loc`),
    },
  ];
  voisinesEtendues(slug, 20)
    .filter((n) => RENTAL_SET.has(n))
    .slice(0, 6)
    .forEach((n) =>
      links.push({
        href: rentalHref(n, locale),
        label: ancreLocation(n, locale, `${slug}-loc`),
      }),
    );
  return links;
};
